import React, { useEffect, useRef, useState } from 'react';
import { Card, Form, Input, Button, Typography, message, Alert, Space, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import request from '../../../utils/request';
import { checkEmailAvailability } from '../../../services/auth';

const { Title, Text } = Typography;
type EmailCheckStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid';

const AdminCustomerCreate: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [emailCheckStatus, setEmailCheckStatus] = useState<EmailCheckStatus>('idle');
  const [emailCheckText, setEmailCheckText] = useState('');
  const emailCheckSeqRef = useRef(0);
  const watchedEmail = Form.useWatch('email', form);
  // 已移除调试信息

  useEffect(() => {
    const normalizedEmail = String(watchedEmail || '').trim().toLowerCase();
    if (!normalizedEmail) {
      setEmailCheckStatus('idle');
      setEmailCheckText('');
      return;
    }
    if (!normalizedEmail.includes('@')) {
      setEmailCheckStatus('invalid');
      setEmailCheckText('请输入有效邮箱格式');
      return;
    }
    setEmailCheckStatus('checking');
    setEmailCheckText('正在检查邮箱可用性...');
    const currentSeq = ++emailCheckSeqRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkEmailAvailability(normalizedEmail);
        if (currentSeq !== emailCheckSeqRef.current) return;
        if (result.available) {
          setEmailCheckStatus('available');
          setEmailCheckText(result.detail || '该邮箱可用');
        } else {
          setEmailCheckStatus('unavailable');
          setEmailCheckText(result.detail || '该邮箱已被占用');
        }
      } catch (err: any) {
        if (currentSeq !== emailCheckSeqRef.current) return;
        setEmailCheckStatus('unavailable');
        setEmailCheckText(err?.response?.data?.detail || '邮箱不可用，请更换后重试');
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [watchedEmail]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const normalizedEmail = String(values.email || '').trim().toLowerCase();
      if (normalizedEmail) {
        const emailResult = await checkEmailAvailability(normalizedEmail);
        if (!emailResult.available) {
          message.error(emailResult.detail || '该邮箱已被占用');
          setEmailCheckStatus('unavailable');
          setEmailCheckText(emailResult.detail || '该邮箱已被占用');
          return;
        }
      }
      setSubmitting(true);

      // 组装创建客户主账号的payload
      const payload: any = {
        username: values.username || undefined,
        email: values.email || undefined,
        password: values.password,
        role: 'customer',
        profile: {
          customer_sub_role: 'main_account',
        }
      };

      // 最小化日志

      // 管理员专属接口
      // 规范化网站：若无协议前缀，则自动补全 http://
      const websiteRaw: string | undefined = values.website?.trim();
      const websiteNormalized = websiteRaw
        ? (/^https?:\/\//i.test(websiteRaw) ? websiteRaw : `http://${websiteRaw}`)
        : undefined;

      // 将社交媒体列表转成字典结构传给后端
      const socialList = Array.isArray(values.social_accounts_list) ? values.social_accounts_list : [];
      const socialDict = socialList.reduce((acc: any, cur: any) => {
        if (cur && cur.platform && cur.account) acc[cur.platform] = cur.account;
        return acc;
      }, {} as any);
      const mainProductCategories = (Array.isArray(values.main_product_categories) ? values.main_product_categories : [])
        .map((x: any) => String(x || '').trim())
        .filter((x: string) => !!x)
        .slice(0, 3);
      const salesPlatformLinks = (Array.isArray(values.sales_platform_links) ? values.sales_platform_links : [])
        .map((x: any) => String(x || '').trim())
        .filter((x: string) => !!x)
        .slice(0, 3);

      const resp = await request.post('/api/v1/users/admin/customers/', {
        username: payload.username,
        email: payload.email,
        password: payload.password,
        // 新增可选扩展字段（后端将写入到UserProfile）
        company_name: values.company_name || undefined,
        phone_country_code: values.phone_country_code || undefined,
        phone_number: values.phone_number || undefined,
        personal_name: values.personal_name || undefined,
        main_product_categories: mainProductCategories.length ? mainProductCategories : undefined,
        sales_platform_links: salesPlatformLinks.length ? salesPlatformLinks : undefined,
        social_accounts: Object.keys(socialDict).length > 0 ? socialDict : undefined,
        website: websiteNormalized,
        notes: values.notes || undefined,
      });
      //
      if (resp && (resp.status === 201 || resp.data?.id)) {
        message.success('客户创建成功');
        // 创建成功后返回客户列表或客户管理页
        navigate('/dashboard/admin/customers/list', { replace: true });
      } else {
        message.success('提交成功');
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data || e?.message;
      let errMsg = detail || '创建失败';
      //
      if (status === 403) errMsg = '无权限：仅管理员可通过此接口创建客户主账号';
      if (status === 401) errMsg = '未登录或登录已过期，请重新登录后再试';
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <Title level={4} style={{ margin: 0 }}>创建客户</Title>
        </div>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="说明"
          description={
            <div>
              本页面用于管理员创建客户主账号，客户编码由系统自动生成，客户子账号只能由客户主账号自行创建。
            </div>
          }
        />

        <Form
          form={form}
          layout="vertical"
          initialValues={{}}
        >
          <Form.Item
            label="邮箱"
            name="email"
            validateStatus={
              emailCheckStatus === 'available'
                ? 'success'
                : emailCheckStatus === 'checking'
                  ? 'validating'
                  : emailCheckStatus === 'unavailable' || emailCheckStatus === 'invalid'
                    ? 'error'
                    : undefined
            }
            help={
              emailCheckText ? (
                <span
                  style={{
                    color: emailCheckStatus === 'available' ? '#16a34a' : undefined,
                  }}
                >
                  {emailCheckText}
                </span>
              ) : undefined
            }
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '邮箱格式不正确' },
            ]}
          > 
            <Input placeholder="必填：邮箱" allowClear />
          </Form.Item>

          <Form.Item label="初始密码" name="password" rules={[{ required: true, message: '请输入初始密码' }, { min: 6, message: '至少6位' }]}> 
            <Input.Password placeholder="请输入初始密码" />
          </Form.Item>

          <Form.Item
            label="验证密码"
            name="confirm_password"
            dependencies={['password']}
            rules={[
              { required: true, message: '请再次输入密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入初始密码" />
          </Form.Item>

          <Form.Item label="用户名（可选）" name="username"> 
            <Input placeholder="可选：不填写则保持空值（最多20位）" allowClear maxLength={20} />
          </Form.Item>

          <Form.Item label="公司名称" name="company_name">
            <Input placeholder="公司中文或英文名称（可含数字）" allowClear />
          </Form.Item>

          <Form.Item label="公司网站" name="website" rules={[{ type: 'url', message: '请输入合法的网址' }]}> 
            <Input placeholder="https://example.com" allowClear />
          </Form.Item>

          <Form.Item label="个人姓名" name="personal_name">
            <Input placeholder="可选" allowClear maxLength={80} />
          </Form.Item>

          <Form.List name="main_product_categories">
            {(fields, { add, remove }) => (
              <Form.Item
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span>主营产品类目</span>
                    <Button
                      type="dashed"
                      size="small"
                      style={{ marginLeft: 30 }}
                      onClick={() => add()}
                      disabled={fields.length >= 3}
                    >
                      添加类目
                    </Button>
                  </span>
                }
              >
                {fields.map((field, idx) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...field}
                      rules={[{ required: true, message: '请输入类目' }]}
                      style={{ minWidth: 360 }}
                    >
                      <Input placeholder={`主营产品类目 ${idx + 1}`} allowClear />
                    </Form.Item>
                    <Button danger onClick={() => remove(field.name)}>删除</Button>
                  </Space>
                ))}
              </Form.Item>
            )}
          </Form.List>

          <Form.List name="sales_platform_links">
            {(fields, { add, remove }) => (
              <Form.Item
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span>销售平台店铺链接或产品链接</span>
                    <Button
                      type="dashed"
                      size="small"
                      style={{ marginLeft: 30 }}
                      onClick={() => add()}
                      disabled={fields.length >= 3}
                    >
                      添加链接
                    </Button>
                  </span>
                }
              >
                {fields.map((field, idx) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...field}
                      rules={[{ required: true, message: '请输入链接' }]}
                      style={{ minWidth: 360 }}
                    >
                      <Input placeholder={`链接 ${idx + 1}`} allowClear />
                    </Form.Item>
                    <Button danger onClick={() => remove(field.name)}>删除</Button>
                  </Space>
                ))}
              </Form.Item>
            )}
          </Form.List>

          <Form.Item label="电话国家码" name="phone_country_code">
            <Input placeholder="例如：+86 / +1" allowClear maxLength={8} />
          </Form.Item>

          <Form.Item label="电话号码" name="phone_number">
            <Input placeholder="不含国家码的电话号码" allowClear maxLength={30} />
          </Form.Item>

          <Form.List name="social_accounts_list">
            {(fields, { add, remove }) => (
              <Form.Item
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span>社交媒体账号</span>
                    <Button type="dashed" size="small" style={{ marginLeft: 30 }} onClick={() => add()}>
                      添加社交媒体
                    </Button>
                  </span>
                }
                tooltip="可多选并填写对应账号/链接"
              >
                <div>
                  {fields.map(field => (
                    <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'platform']}
                        fieldKey={[field.fieldKey!, 'platform']}
                        rules={[{ required: true, message: '请选择平台' }]}
                        style={{ minWidth: 160 }}
                      >
                        <Select
                          placeholder="选择平台"
                          options={[
                            { label: '微信', value: 'wechat' },
                            { label: 'QQ', value: 'qq' },
                            { label: 'WhatsApp', value: 'whatsapp' },
                            { label: 'Telegram', value: 'telegram' },
                            { label: 'Facebook', value: 'facebook' },
                            { label: 'LinkedIn', value: 'linkedin' },
                            { label: 'Twitter/X', value: 'twitter' },
                            { label: '抖音', value: 'douyin' },
                            { label: '小红书', value: 'xiaohongshu' },
                            { label: '其他', value: 'other' },
                          ]}
                          showSearch
                          optionFilterProp="label"
                          allowClear
                        />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, 'account']}
                        fieldKey={[field.fieldKey!, 'account']}
                        rules={[{ required: true, message: '请输入账号或链接' }]}
                        style={{ minWidth: 280 }}
                      >
                        <Input placeholder="账号/链接" allowClear />
                      </Form.Item>
                      <Button danger onClick={() => remove(field.name)}>删除</Button>
                    </Space>
                  ))}
                </div>
              </Form.Item>
            )}
          </Form.List>

          <Form.Item label="备注信息" name="notes">
            <Input.TextArea placeholder="可选备注信息" rows={3} />
          </Form.Item>

          {/* 子角色选择已取消，默认创建主账号 */}

          <Space>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>提交</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default AdminCustomerCreate;


